import React, { useState, useContext } from 'react';
import styled from 'astroturf/react';
import { Body } from 'js/design_system/Body';
import { Button } from 'js/design_system/Button';
import { TextArea } from 'js/design_system/forms/TextArea';
import { Icon } from 'js/design_system/Icon';
import { CiterightContext } from '../CiterightContextProvider';
import { CiterightLandingLayout } from './CiterightLandingLayout';

const InputSection = styled.div`
  background: var(--background-white);
  border: 1px solid var(--stroke-light);
  border-radius: var(--border-radius-radius-md);
  padding: var(--spacing-sm-32);
  margin-bottom: var(--spacing-sm-24);
`;

const InputLabel = styled.label`
  display: block;
  font-size: var(--type-body-sm-size);
  font-weight: var(--type-font-weight-semibold);
  color: var(--text-secondary);
  margin-bottom: var(--spacing-xs-8);
`;

const StyledTextArea = styled(TextArea)`
  width: 100%;
  min-height: 180px;
  font-size: var(--type-body-md-size);
  line-height: 1.5;
  resize: vertical;
`;

const CharacterCount = styled(Body).attrs({
  size: 'sm',
})`
  color: var(--brand-colors-neutral-800);
  margin-top: var(--spacing-xs-8);
`;

const SubmitButton = styled(Button)`
  width: 100%;
`;

const ErrorMessage = styled.div`
  background: var(--brand-colors-error-100);
  border: 1px solid var(--brand-colors-error-500);
  border-radius: var(--border-radius-radius-md);
  padding: var(--spacing-xs-16);
  margin-bottom: var(--spacing-sm-24);
  color: var(--brand-colors-error-900);
  font-size: var(--type-body-sm-size);
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-xs-8);
`;

export const LandingScreen: React.FC = () => {
  const [inputText, setInputText] = useState('');
  const context = useContext(CiterightContext);

  const handleSubmit = async () => {
    if (!inputText.trim() || !context?.startCitationReport) return;

    // Use context method to start citation report
    await context.startCitationReport(inputText.trim());
  };

  const isSubmitting = context?.report?.done === false;
  const error = context?.error;

  return (
    <CiterightLandingLayout
      subtitle="Get citation suggestions for your academic writing"
      infoBannerTitle="How CiteRight works"
      infoBannerDescription="Paste any text from your paper. We'll find suggestions for the overall text and identify individual claims that need citations."
    >
      <InputSection>
        <InputLabel htmlFor="citeright-text-input">
          Paste text from your paper
        </InputLabel>
        <StyledTextArea
          id="citeright-text-input"
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          placeholder="Enter or paste your text here (e.g., a few paragraphs from your research paper)..."
        />
        <CharacterCount>
          {inputText.length} characters • We'll identify claims that need
          citations
        </CharacterCount>
      </InputSection>

      {error && (
        <ErrorMessage>
          <Icon name="error" size={20} />
          <span>{error}</span>
        </ErrorMessage>
      )}

      <SubmitButton
        onClick={handleSubmit}
        disabled={!inputText.trim() || isSubmitting}
        size="lg"
      >
        {isSubmitting ? 'Searching...' : 'Search for Citations'}
      </SubmitButton>
    </CiterightLandingLayout>
  );
};

export default LandingScreen;
